<template>
  <div>
    <!-- Floating Cart Badge -->
    <div
      v-if="showBadge"
      class="estimate-cart-badge"
      :class="{ 'pulse': shouldPulse }"
      @click="handleClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      title="View Current Estimate"
    >
      <div class="cart-icon">
        <font-awesome-icon icon="fa-solid fa-cart-shopping" />
        <span v-if="itemCount > 0" class="cart-count">{{ itemCount }}</span>
      </div>
      <div v-if="showDetails" class="cart-details">
        <div class="cart-total">{{ format(total) }}</div>
        <div class="cart-items">{{ itemCount }} {{ itemCount === 1 ? 'item' : 'items' }}</div>
      </div>
    </div>

    <!-- Current Estimate Modal -->
    <CurrentEstimateModal
      v-model="showModal"
      @close="handleModalClose"
      @saved="handleEstimateSaved"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useEstimates } from '@/composables/useEstimates'
import { useCurrency } from '@/composables/useCurrency'
import CurrentEstimateModal from '@/components/forms/CurrentEstimateModal.vue'

export default {
  name: 'EstimateCartBadge',
  components: {
    CurrentEstimateModal
  },
  setup() {
    const store = useStore()
    const { syncWithBackend, currentEstimateItemCount, currentEstimateTotal } = useEstimates()
    const { format } = useCurrency()

    const showBadge = ref(true) // Always visible as per requirements
    const showDetails = ref(false)
    const shouldPulse = ref(false)
    const showModal = ref(false)

    const itemCount = computed(() => currentEstimateItemCount.value)
    const total = computed(() => currentEstimateTotal.value)

    // Pulse animation when item count changes
    watch(itemCount, (newCount, oldCount) => {
      if (newCount > oldCount && oldCount !== undefined) {
        shouldPulse.value = true
        setTimeout(() => {
          shouldPulse.value = false
        }, 600)
      }
    })

    const handleClick = () => {
      showModal.value = true
      showDetails.value = false // Hide details when opening modal
    }

    const handleModalClose = () => {
      showModal.value = false
    }

    // Open the cart when another screen requests it (e.g. add-to-estimate toast)
    watch(() => store.getters['estimates/cartOpen'], (open) => {
      if (open) {
        showModal.value = true
        showDetails.value = false
        store.commit('estimates/SET_CART_OPEN', false)
      }
    })

    const handleEstimateSaved = () => {
      showModal.value = false
      // The estimate has been saved, sync with backend will happen automatically
    }

    const handleMouseEnter = () => {
      if (!showModal.value) {
        showDetails.value = true
      }
    }

    const handleMouseLeave = () => {
      showDetails.value = false
    }

    // Sync with backend on mount
    onMounted(async () => {
      try {
        await syncWithBackend()
      } catch (err) {
        console.error('Failed to sync estimate on mount:', err)
      }
    })

    return {
      showBadge,
      showDetails,
      shouldPulse,
      showModal,
      itemCount,
      total,
      format,
      handleClick,
      handleModalClose,
      handleEstimateSaved,
      handleMouseEnter,
      handleMouseLeave
    }
  }
}
</script>

<style scoped>
.estimate-cart-badge {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background-color: #4a69bd;
  color: #ffffff;
  border-radius: 50px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(74, 105, 189, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.estimate-cart-badge:hover {
  background-color: #3d5aa0;
  box-shadow: 0 6px 16px rgba(74, 105, 189, 0.5);
  transform: translateY(-2px);
}

.estimate-cart-badge.pulse {
  animation: pulse 0.6s ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.cart-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.cart-icon svg {
  font-size: 20px;
}

.cart-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ef4444;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #4a69bd;
}

.cart-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 80px;
}

.cart-total {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
}

.cart-items {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .estimate-cart-badge {
    bottom: 20px;
    right: 20px;
    padding: 10px 14px;
  }

  .cart-icon {
    width: 24px;
    height: 24px;
  }

  .cart-icon svg {
    font-size: 18px;
  }

  .cart-count {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .cart-details {
    min-width: 70px;
  }

  .cart-total {
    font-size: 14px;
  }

  .cart-items {
    font-size: 11px;
  }
}
</style>
